/**
 * Visual Thermometer component for liquidity dashboard.
 * Shows current value relative to break-even and profit target.
 *
 * Props:
 *  - label: string
 *  - value: number (current income)
 *  - breakEven: number
 *  - target: number (revenue target with profit)
 *  - currency: bool
 */
export default function Thermometer({ label, value = 0, breakEven = 0, target = 0, sublabel, testId = "thermometer" }) {
  const maxVal = Math.max(target * 1.1, value * 1.05, breakEven * 1.1, 1);
  const valuePct = Math.max(0, Math.min(100, (value / maxVal) * 100));
  const bePct = Math.max(0, Math.min(100, (breakEven / maxVal) * 100));
  const tgtPct = Math.max(0, Math.min(100, (target / maxVal) * 100));

  const hitBreakEven = value >= breakEven && breakEven > 0;
  const hitTarget = value >= target && target > 0;
  const fillColor = hitTarget ? "#0AA09E" : hitBreakEven ? "#3FB5B0" : "#FF6B5C";

  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 md:p-6" data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display text-2xl md:text-3xl font-semibold mt-1 text-[#1F242E]" data-testid={`${testId}-value`}>
            {fmt(value)}
          </div>
          {sublabel && <div className="text-xs text-[#94A3B8] mt-1">{sublabel}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Status</div>
          <div
            className={`mt-1 text-xs font-medium px-2 py-1 rounded inline-block ${
              hitTarget
                ? "bg-[#0AA09E] text-white"
                : hitBreakEven
                ? "bg-[#E5E7EB] text-[#0AA09E]"
                : "bg-[#FFF1EE] text-[#FF6B5C]"
            }`}
            data-testid={`${testId}-status`}
          >
            {hitTarget ? "Über Ziel" : hitBreakEven ? "Über Break-even" : "Unter Break-even"}
          </div>
        </div>
      </div>

      <div className="flex items-stretch gap-4">
        {/* Vertical thermometer */}
        <div className="relative h-56 w-12 shrink-0">
          {/* Track */}
          <div className="absolute inset-0 rounded-full bg-[#E6F7F7] overflow-hidden">
            {/* Fill from bottom */}
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-500"
              style={{ height: `${valuePct}%`, background: fillColor }}
            />
          </div>
          {/* Break-even marker line */}
          {breakEven > 0 && (
            <div
              className="absolute left-[-6px] right-[-6px] border-t-2 border-dashed border-[#F59E0B]"
              style={{ bottom: `${bePct}%` }}
              title="Break-even"
            />
          )}
          {/* Target marker line */}
          {target > 0 && (
            <div
              className="absolute left-[-6px] right-[-6px] border-t-2 border-[#0AA09E]"
              style={{ bottom: `${tgtPct}%` }}
              title="Zielumsatz"
            />
          )}
          {/* Bulb at bottom */}
          <div
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 h-8 w-8 rounded-full border-4 border-[#E6F7F7]"
            style={{ background: fillColor }}
          />
        </div>
        {/* Legend / values */}
        <div className="flex flex-col justify-between py-1 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-0 border-t-2 border-[#0AA09E]"></span>
              <span className="text-xs text-[#5B6371]">Zielumsatz</span>
            </div>
            <div className="font-medium text-[#1F242E] ml-5" data-testid={`${testId}-target`}>{fmt(target)}</div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-0 border-t-2 border-dashed border-[#F59E0B]"></span>
              <span className="text-xs text-[#5B6371]">Break-even</span>
            </div>
            <div className="font-medium text-[#1F242E] ml-5" data-testid={`${testId}-breakeven`}>{fmt(breakEven)}</div>
          </div>
          <div>
            <div className="text-xs text-[#5B6371]">Aktuell</div>
            <div className="font-medium ml-0" style={{ color: fillColor }}>{fmt(value)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
